import React, { useContext, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip
} from "@mui/material";
import { ChecklistRtl, Add, Edit, Visibility } from "@mui/icons-material";
import AppBarMenu from "../../components/AppBarMenu";
import { ApiDataContext } from "../../contexts/ApiDataContext";
import TemplateChecklistEditor from "../../components/admin/templateChecklist/TemplateChecklistEditor";
import TemplateChecklistViewer from "../../components/admin/templateChecklist/TemplateChecklistViewer";
import TemplateChecklistSkeleton from "../../components/shared/loader/TemplateChecklistSkeleton";
import { useFetchAllTemplateChecklists } from "../../hooks/api/templateChecklistApiHooks";

export default function TemplateChecklistManagementPage() {
  const { apiData } = useContext(ApiDataContext);
  const [selectedPdcId, setSelectedPdcId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);


  // 🚀 UMA ÚNICA CHAMADA EFICIENTE para todos os templates
  const { allTemplatesLoading, getTemplateStatusForPdc } = useFetchAllTemplateChecklists();

  const handleCreateTemplate = (pdcId) => {
    const status = getTemplateStatusForPdc(pdcId);
    
    if (status.exists) {
      console.warn(`Template já existe para PDC ${pdcId}:`, status.templates);
      return;
    }
    
    setSelectedPdcId(pdcId);
    setSelectedTemplate(null);
    setIsCreating(true);
    setEditorOpen(true);
  };

  const handleEditTemplate = (pdcId) => {
    const status = getTemplateStatusForPdc(pdcId);
    
    if (status.exists && status.templates.length > 0) {
      const template = status.templates[0];
      setSelectedPdcId(pdcId);
      setSelectedTemplate(template);
      setIsCreating(false);
      setEditorOpen(true);
    }
  };

  const handleViewTemplate = (pdcId) => {
    const status = getTemplateStatusForPdc(pdcId);
    
    if (status.exists && status.templates.length > 0) {
      const template = status.templates[0];
      setSelectedTemplate(template);
      setViewerOpen(true);
    }
  };

  const getTemplateStatus = (pdcId) => {
    if (allTemplatesLoading) {
      return { status: 'Carregando...', color: 'default' };
    }
    
    const status = getTemplateStatusForPdc(pdcId);
    
    return status.exists 
      ? { status: 'Configurado', color: 'success', count: status.count }
      : { status: 'Pendente', color: 'warning' };
  };

  // 🎯 Determinar se deve mostrar loading
  const isLoading = !apiData || allTemplatesLoading;

  return (
    <AppBarMenu>
      {isLoading ? (
        /* 🎨 Loading interno usando skeleton - navbar permanece visível */
        <TemplateChecklistSkeleton />
      ) : (
        <Container maxWidth={false}>
          <Box sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ChecklistRtl sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Gestão de Templates de Checklist
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              Gerencie templates de checklist por concessionária. Cada template será usado como base 
              para criar checklists em novos projetos.
            </Typography>

            <Grid container spacing={3}>
              {apiData.powerDistributionCompanies?.map((pdc) => {
                const status = getTemplateStatus(pdc.id);
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={pdc.id}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" component="h2" sx={{ flexGrow: 1, fontWeight: 600 }}>
                            {pdc.name}
                          </Typography>
                          <Chip 
                            label={status.status} 
                            color={status.color} 
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Estado: {pdc.states?.name || 'N/A'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          Configure o template de checklist padrão para projetos desta concessionária.
                          {status.count > 0 && (
                            <Typography component="span" variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                              {status.count} template(s) configurado(s)
                            </Typography>
                          )}
                        </Typography>
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                          {status.status === 'Configurado' ? (
                            <>
                              <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => handleViewTemplate(pdc.id)}
                                sx={{ flex: 1 }}
                              >
                                Visualizar
                              </Button>
                              <Button
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleEditTemplate(pdc.id)}
                                variant="contained"
                                sx={{ flex: 1 }}
                              >
                                Editar
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="small"
                              startIcon={<Add />}
                              onClick={() => handleCreateTemplate(pdc.id)}
                              variant="contained"
                              color="primary"
                              sx={{ width: '100%' }}
                            >
                              Criar Template
                            </Button>
                          )}
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Container>
      )}

      {/* Editor Dialog */}
      <TemplateChecklistEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        pdcId={selectedPdcId}
        template={selectedTemplate}
        isCreating={isCreating}
      />

      {/* Viewer Dialog */}
      <TemplateChecklistViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        template={selectedTemplate}
      />
    </AppBarMenu>
  );
}